import * as path from "node:path";
import * as ts from "typescript";
import {
  ExtractFencedSamplesOptions,
  FencedSampleRecord,
  SampleLang,
  extractFencedSamples,
} from "./extraction";

/**
 * One row in the sample-compilation report — describes a single
 * compilation failure. Compiles that succeeded are intentionally
 * absent: the docs-sync scan phase consumes failures only and treats
 * an empty array as "every sample compiled cleanly".
 */
export interface SampleCompilationFailure {
  /**
   * Path to the markdown file the sample was extracted from. Resolved
   * relative to the configured `docsRoot` so reports are stable
   * across machines.
   */
  readonly docPath: string;

  /**
   * 1-indexed line number of the opening fence in the source markdown
   * file (the line that begins with the three backticks).
   */
  readonly line: number;

  /**
   * 0-indexed position of this fenced block among all fenced TS-shaped
   * blocks in the same markdown file.
   */
  readonly fenceIndex: number;

  /**
   * Language tag the sample carried — one of `ts`, `typescript`,
   * `tsx`, or `typescriptreact`.
   */
  readonly lang: SampleLang;

  /**
   * Flat array of compiler diagnostics, one entry per `tsc` finding.
   * Each entry is the message text plus any code prefix the compiler
   * surfaced — formatted to be self-contained so the consuming scan
   * report can render the failure without re-running the compiler.
   */
  readonly diagnostics: Array<string>;
}

/**
 * Options accepted by {@link compileFencedSamples}.
 */
export interface CompileFencedSamplesOptions extends ExtractFencedSamplesOptions {
  /**
   * Override the {@link ts.CompilerOptions} the helper uses when
   * compiling each sample. Merged on top of the helper's defaults
   * (see {@link DEFAULT_SAMPLE_COMPILER_OPTIONS}). Override sparingly:
   * the defaults are tuned to be permissive on prose samples (no
   * unused-locals/unused-parameters errors, JSX preserved for tsx,
   * lib targets DOM/ES2022). Setting an option to `undefined` here
   * removes the helper's default for that key.
   */
  readonly compilerOptions?: ts.CompilerOptions;

  /**
   * When `true`, diagnostics carrying TS error code `2307`
   * ("Cannot find module …") are demoted from failures to silent
   * skips for repo-relative module specifiers (paths starting with
   * `./`, `../`, or `/`). Bare specifiers like `react` or
   * `@scope/pkg` are still treated as failures because they
   * indicate genuinely missing imports rather than docs-relative
   * sample drift.
   *
   * @default true
   */
  readonly tolerateRepoRelativeMissingModule?: boolean;
}

/**
 * Default compiler options applied to every fenced sample. Tuned to
 * be permissive on prose samples while still surfacing real
 * compilation errors:
 *
 * - `target` / `module` mirror modern Node + ES module conventions.
 * - `lib` carries `DOM` so samples that mention `console`,
 *   `document`, or `window` compile cleanly.
 * - `strict` stays on so the helper still catches type errors.
 * - `noUnusedLocals` / `noUnusedParameters` stay **off** because
 *   prose samples often declare a name purely to demonstrate a
 *   shape; flagging that as a failure would block useful docs.
 * - `skipLibCheck` is on so a missing `.d.ts` in a transitive
 *   dependency does not poison the suite.
 * - `jsx` preserves the JSX so `tsx` fenced blocks compile.
 * - `noEmit` is the whole point — we only need diagnostics.
 */
export const DEFAULT_SAMPLE_COMPILER_OPTIONS: ts.CompilerOptions = {
  target: ts.ScriptTarget.ES2022,
  module: ts.ModuleKind.ESNext,
  moduleResolution: ts.ModuleResolutionKind.Bundler,
  lib: ["lib.es2022.d.ts", "lib.dom.d.ts"],
  strict: true,
  noUnusedLocals: false,
  noUnusedParameters: false,
  skipLibCheck: true,
  esModuleInterop: true,
  resolveJsonModule: true,
  isolatedModules: true,
  jsx: ts.JsxEmit.Preserve,
  noEmit: true,
  allowSyntheticDefaultImports: true,
};

const REPO_RELATIVE_MODULE_PATTERN = /^['"](?:\.\.?\/|\/)/;

/**
 * Walks every `*.md` file under the configured docs root, extracts
 * fenced TypeScript samples, and runs the TypeScript compiler against
 * each non-skipped sample. Returns one record per failing sample —
 * passing samples are intentionally absent from the result.
 *
 * Each sample is compiled in its own in-memory program so unrelated
 * blocks never share scope. Skipped samples (fence meta `skip` /
 * `no-check`, or first line `// @no-check`) are passed through
 * unchecked.
 *
 * The helper does not invoke `tsc` as a subprocess — it uses the TS
 * compiler API directly with the host's `typescript` peer dependency.
 * That means a single test or scan run produces a single TypeScript
 * Program per sample, which is fast enough for the scan-phase budget
 * but does mean callers must not pass it the full repo's `docs/`
 * tree at unit-test time. Tests stay on small inline tmpdir fixtures.
 *
 * @param options - Compilation options. All fields are optional;
 *                  pass an empty object to use defaults.
 */
export function compileFencedSamples(
  options: CompileFencedSamplesOptions = {},
): Array<SampleCompilationFailure> {
  const samples = extractFencedSamples(options);
  const tolerateRepoRelative =
    options.tolerateRepoRelativeMissingModule ?? true;
  const failures: Array<SampleCompilationFailure> = [];
  for (const sample of samples) {
    if (sample.skipped) {
      continue;
    }
    const diagnostics = compileSingleSample(
      sample,
      options.compilerOptions,
      tolerateRepoRelative,
    );
    if (diagnostics.length > 0) {
      failures.push({
        docPath: sample.docPath,
        line: sample.line,
        fenceIndex: sample.fenceIndex,
        lang: sample.lang,
        diagnostics,
      });
    }
  }
  return failures;
}

function compileSingleSample(
  sample: FencedSampleRecord,
  overrides: ts.CompilerOptions | undefined,
  tolerateRepoRelative: boolean,
): Array<string> {
  const filename = sampleFilename(sample);
  const compilerOptions = mergeCompilerOptions(overrides);
  const host = createSingleFileHost(filename, sample.code, compilerOptions);
  const program = ts.createProgram({
    rootNames: [filename],
    options: compilerOptions,
    host,
  });
  const sourceFile = program.getSourceFile(filename);
  const collected: Array<ts.Diagnostic> = [];
  if (sourceFile) {
    collected.push(...program.getSyntacticDiagnostics(sourceFile));
    collected.push(...program.getSemanticDiagnostics(sourceFile));
  } else {
    collected.push(...program.getSyntacticDiagnostics());
    collected.push(...program.getSemanticDiagnostics());
  }
  const diagnostics: Array<string> = [];
  for (const d of collected) {
    if (tolerateRepoRelative && isRepoRelativeMissingModule(d, sample.code)) {
      continue;
    }
    diagnostics.push(formatDiagnostic(d));
  }
  return diagnostics;
}

function sampleFilename(sample: FencedSampleRecord): string {
  const safeDoc = sample.docPath.replace(/[^a-zA-Z0-9]+/g, "_");
  const ext =
    sample.lang === SampleLang.Tsx || sample.lang === SampleLang.Typescriptreact
      ? "tsx"
      : "ts";
  // Use a virtual absolute path so module resolution stays predictable.
  return path.posix.join("/", `${safeDoc}__${sample.fenceIndex}.${ext}`);
}

function mergeCompilerOptions(
  overrides: ts.CompilerOptions | undefined,
): ts.CompilerOptions {
  if (!overrides) {
    return { ...DEFAULT_SAMPLE_COMPILER_OPTIONS };
  }
  const merged: ts.CompilerOptions = { ...DEFAULT_SAMPLE_COMPILER_OPTIONS };
  for (const key of Object.keys(overrides)) {
    const value = overrides[key];
    if (value === undefined) {
      delete merged[key];
    } else {
      merged[key] = value;
    }
  }
  return merged;
}

function createSingleFileHost(
  filename: string,
  source: string,
  compilerOptions: ts.CompilerOptions,
): ts.CompilerHost {
  // Start from the default host so lib.*.d.ts files resolve from the
  // installed `typescript` package — the helper relies on the peer
  // dependency's bundled libs to type-check `console`, `Promise`,
  // and other globals.
  const baseHost = ts.createCompilerHost(compilerOptions, true);
  return {
    ...baseHost,
    getSourceFile(name, languageVersionOrOptions, onError, shouldCreate) {
      if (name === filename) {
        return ts.createSourceFile(
          name,
          source,
          languageVersionOrOptions,
          true,
        );
      }
      return baseHost.getSourceFile(
        name,
        languageVersionOrOptions,
        onError,
        shouldCreate,
      );
    },
    fileExists(name) {
      if (name === filename) {
        return true;
      }
      return baseHost.fileExists(name);
    },
    readFile(name) {
      if (name === filename) {
        return source;
      }
      return baseHost.readFile(name);
    },
    writeFile() {
      // noEmit — never write anything.
    },
    getCanonicalFileName(name) {
      return baseHost.getCanonicalFileName(name);
    },
    useCaseSensitiveFileNames() {
      return baseHost.useCaseSensitiveFileNames();
    },
    getNewLine() {
      return baseHost.getNewLine();
    },
    getDefaultLibFileName(o) {
      return baseHost.getDefaultLibFileName(o);
    },
    getCurrentDirectory() {
      return baseHost.getCurrentDirectory();
    },
  };
}

function isRepoRelativeMissingModule(d: ts.Diagnostic, code: string): boolean {
  if (d.code !== 2307) {
    return false;
  }
  // The diagnostic's textual form contains the module specifier
  // wrapped in single quotes. `ts.flattenDiagnosticMessageText` is the
  // canonical accessor for the message regardless of chained
  // diagnostic shape.
  const text = ts.flattenDiagnosticMessageText(d.messageText, "\n");
  // Pull the module specifier out of the message: shape is
  // `Cannot find module 'X' or its corresponding type declarations.`
  const match = /'([^']+)'/.exec(text);
  if (!match) {
    return false;
  }
  const specifier = match[1];
  if (!specifier) {
    return false;
  }
  // Match the specifier as a quoted string against the source so we
  // also catch the case where the message used double quotes.
  const inSourceSingle = `'${specifier}'`;
  const inSourceDouble = `"${specifier}"`;
  if (!code.includes(inSourceSingle) && !code.includes(inSourceDouble)) {
    return false;
  }
  return REPO_RELATIVE_MODULE_PATTERN.test(`'${specifier}'`);
}

function formatDiagnostic(d: ts.Diagnostic): string {
  const text = ts.flattenDiagnosticMessageText(d.messageText, " ");
  const code = d.code === undefined ? "" : `TS${d.code}: `;
  return `${code}${text}`.trim();
}
